import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { environment } from '../config/environment';
import { getApiHeadersForGet } from '../utils/apiUtils';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  Button,
  Chip,
  Divider,
  Snackbar,
  Switch,
  FormControlLabel,
  Paper,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GiftCardProvider {
  id: string;
  name: string;
  min_amount: number;
  max_amount: number;
}

interface PlatformConfig {
  _id?: string;
  withdrawal_threshold_cash: number;
  withdrawal_threshold_points: number;
  points_per_dollar: number;
  enabled_payout_types: string[];
  gift_card_providers: GiftCardProvider[];
  secondary_prize_default_type: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getAuthHeaders = () => {
  const base = getApiHeadersForGet() as Record<string, string>;
  return { ...base, 'Content-Type': 'application/json' };
};

const PAWWEBAPP_URL = environment.petflixApiUrl;

// ─── Component ────────────────────────────────────────────────────────────────

const PlatformSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [config, setConfig]     = useState<PlatformConfig | null>(null);
  const [form, setForm]         = useState<Partial<PlatformConfig>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch via pet-contests platform-settings proxy (or direct pawwebapp if accessible)
      const response = await fetch(`${environment.apiBaseUrl}/platform-settings`, {
        headers: getApiHeadersForGet() as HeadersInit,
      });
      if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
      const data: PlatformConfig = await response.json();
      setConfig(data);
      setForm({
        withdrawal_threshold_cash:   data.withdrawal_threshold_cash,
        withdrawal_threshold_points: data.withdrawal_threshold_points,
        points_per_dollar:           data.points_per_dollar,
        enabled_payout_types:        [...(data.enabled_payout_types ?? [])],
        secondary_prize_default_type: data.secondary_prize_default_type,
      });
    } catch (err: any) {
      setError(err.message || 'Error loading platform settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${environment.apiBaseUrl}/platform-settings`, {
        method: 'PATCH',
        headers: getAuthHeaders() as HeadersInit,
        body: JSON.stringify(form),
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      setSnackbar({ open: true, message: 'Platform settings saved successfully', severity: 'success' });
      await loadConfig();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Error saving settings', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const togglePayoutType = (type: string) => {
    setForm(prev => {
      const types = prev.enabled_payout_types ?? [];
      return {
        ...prev,
        enabled_payout_types: types.includes(type) ? types.filter(t => t !== type) : [...types, type],
      };
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4, minHeight: '100vh' }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Chip icon={<ArrowBackIcon />} label="Back" onClick={() => navigate(-1)} color="primary" clickable />
      </Container>
    );
  }

  return (
    <Container className="napc-page" maxWidth="md" sx={{ py: { xs: 3, md: 6 }, minHeight: '100vh' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Box className="napc-eyebrow">Economics & Payouts</Box>
          <Typography variant="h4" component="h1" fontWeight="bold">
            <SettingsIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2rem' }} />
            Platform Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure withdrawal thresholds, payout types and reward points conversion.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<RefreshIcon />} variant="outlined" onClick={loadConfig}>
            Refresh
          </Button>
          <Chip icon={<ArrowBackIcon />} label="Back" onClick={() => navigate(-1)} color="primary" clickable />
        </Box>
      </Box>

      <Grid container spacing={3}>

        {/* Withdrawal Thresholds */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Withdrawal Thresholds
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Minimum balance a user must accumulate before they can request a withdrawal.
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Cash Threshold (USD)"
                    type="number"
                    inputProps={{ min: 1, step: 1 }}
                    value={form.withdrawal_threshold_cash ?? ''}
                    onChange={e => setForm(p => ({ ...p, withdrawal_threshold_cash: Number(e.target.value) }))}
                    helperText="Minimum USD balance to request cash or gift card withdrawal"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Points Threshold"
                    type="number"
                    inputProps={{ min: 1, step: 10 }}
                    value={form.withdrawal_threshold_points ?? ''}
                    onChange={e => setForm(p => ({ ...p, withdrawal_threshold_points: Number(e.target.value) }))}
                    helperText="Minimum reward points to redeem"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Points Conversion */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Reward Points Conversion
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Points per Dollar"
                    type="number"
                    inputProps={{ min: 1, step: 1 }}
                    value={form.points_per_dollar ?? ''}
                    onChange={e => setForm(p => ({ ...p, points_per_dollar: Number(e.target.value) }))}
                    helperText="e.g. 10 = 10 points equals $1 USD"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Enabled Payout Types */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Enabled Payout Types
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Toggle which payout methods are available for admin selection.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(['cash', 'gift_card', 'rewards_points'] as const).map(type => (
                  <Paper key={type} variant="outlined" sx={{ px: 2, py: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={(form.enabled_payout_types ?? []).includes(type)}
                          onChange={() => togglePayoutType(type)}
                        />
                      }
                      label={type === 'cash' ? 'Cash' : type === 'gift_card' ? 'Gift Card' : 'Reward Points'}
                    />
                  </Paper>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Gift Card Providers (read-only summary) */}
        {config?.gift_card_providers && config.gift_card_providers.length > 0 && (
          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Gift Card Providers
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configured providers (edit directly in database to change).
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {config.gift_card_providers.map(p => (
                    <Chip
                      key={p.id}
                      label={`${p.name} ($${p.min_amount}–$${p.max_amount})`}
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Save */}
        <Grid size={12}>
          <Divider sx={{ mb: 2 }} />
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            disabled={saving}
            onClick={handleSave}
          >
            Save Settings
          </Button>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PlatformSettingsPage;
